import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getEventFacets } from "@/api/event-graph";
import { findEvents, listGatheringKinds } from "@/api/event-about";

/**
 * Find events by what their organisers confirmed, and by the kind of
 * gathering. Only a confirmed fact counts, every fact picked must hold, and
 * an event that said nothing is not listed.
 */
export function EventFinderPanel() {
  const [facets, setFacets] = useState<string[]>([]);
  const [gatheringKind, setGatheringKind] = useState("");
  const catalog = useQuery({ queryKey: ["event-facets"], queryFn: getEventFacets });
  const kinds = useQuery({ queryKey: ["gathering-kinds"], queryFn: listGatheringKinds });
  const found = useQuery({
    queryKey: ["event-finder", facets, gatheringKind],
    queryFn: () => findEvents({ facets, gatheringKind }),
    enabled: facets.length > 0 || gatheringKind !== "",
  });

  function toggle(code: string) {
    setFacets((now) => (now.includes(code) ? now.filter((c) => c !== code) : [...now, code]));
  }

  return (
    <div className="card p-4 space-y-3">
      <h2 className="font-semibold">Find by facts and kind</h2>
      <p className="text-xs text-gray-500">Pick up to 5 facts. GET /events?facets=a,b&gatheringKind=</p>
      <select className="input w-64" value={gatheringKind} onChange={(e) => setGatheringKind(e.target.value)}>
        <option value="">Any kind of gathering</option>
        {(kinds.data?.data.kinds ?? []).map((k) => (
          <option key={k.code} value={k.code}>
            {k.label}
            {k.worship ? " (worship)" : ""}
          </option>
        ))}
      </select>
      <div className="flex flex-wrap gap-1">
        {(catalog.data?.data.facets ?? []).map((f) => (
          <button
            key={f.code}
            title={f.code}
            className={`px-2 py-0.5 rounded border text-xs ${facets.includes(f.code) ? "bg-primary-50 border-primary-400" : "border-gray-300"}`}
            onClick={() => toggle(f.code)}
          >
            {f.label}
          </button>
        ))}
      </div>
      {found.error && <p className="text-xs text-red-600">{(found.error as Error).message}</p>}
      {found.data && (
        <ul className="text-sm text-gray-700 space-y-1">
          {found.data.data.events.length === 0 && <li className="text-gray-500">No event says all of these.</li>}
          {found.data.data.events.map((e) => (
            <li key={e.id}>
              {e.title} · {new Date(e.eventDate).toLocaleDateString()} · {e.city}
              {e.gatheringKind ? ` · ${e.gatheringKind}` : ""}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
